import type { Contract, Milestone, ProviderRequest } from '../types';
import { timelineContracts } from './timelineContracts';
import { isReplacedMilestone } from './milestoneHistory';

/** A project as the lawyer's and inspector's project lists return it. */
export interface ProviderProject {
  id: string;
  projectId?: string;
  projectNumber?: number;
  description?: string;
  status?: string;
  companyName?: string | null;
}

/**
 * One row in the lawyer's or inspector's "Att göra nu":
 * - request: a request to answer (both roles);
 * - reviewContract: a lawyer's contract that is sent but not yet signed by both;
 * - inspectStage: an inspector's stage reported done, waiting for attestation.
 */
export type ProviderTodo =
  | { kind: 'request'; request: ProviderRequest }
  | { kind: 'reviewContract'; project: ProviderProject; contract: Contract }
  | { kind: 'inspectStage'; project: ProviderProject; contract: Contract; milestone: Milestone };

const AWAITING_SIGNATURES = new Set(['draft_sent', 'draft_approved', 'signed_by_company', 'signed_by_person']);

export const providerProjectId = (project: ProviderProject) => project.projectId ?? project.id;

interface ProviderTodoInput {
  providerType: 'lawyer' | 'inspector';
  pendingRequests: ProviderRequest[];
  projects: ProviderProject[];
  contracts: Contract[];
  milestones: Milestone[];
}

/** The rows, requests first, then the projects in their list order. */
export function providerTodos({ providerType, pendingRequests, projects, contracts, milestones }: ProviderTodoInput): ProviderTodo[] {
  const todos: ProviderTodo[] = pendingRequests.map(request => ({ kind: 'request', request }));
  const current = timelineContracts(contracts);
  for (const project of projects) {
    if (project.status === 'completed') continue;
    const projectContracts = current.filter(contract => contract.projectId === providerProjectId(project));
    if (providerType === 'lawyer') {
      projectContracts
        .filter(contract => AWAITING_SIGNATURES.has(contract.draftStatus) && contract.signingStatus !== 'signed')
        .forEach(contract => todos.push({ kind: 'reviewContract', project, contract }));
    } else {
      projectContracts.forEach(contract => milestones
        .filter(milestone => milestone.contractId === contract.id && !isReplacedMilestone(milestone)
          && milestone.deliveryStatus === 'ready_for_attestation')
        .sort((left, right) => left.milestoneNumber - right.milestoneNumber)
        .forEach(milestone => todos.push({ kind: 'inspectStage', project, contract, milestone })));
    }
  }
  return todos;
}
